import { useCallback, useEffect, useRef, useState } from 'react'
import { AimClient, AimError, type AimSession } from '../solo2/aim'
import { NativeSolo2Bridge, solo2Available, wifiStatus } from '../solo2/bridge'
import type { SessionInfo } from '../lib/storage'
import { formatDateTime } from '../lib/dates'
import { currentSsid, joinWifi, wifiAvailable, WifiError } from '../lib/wifi'
import { defaultNick, deviceLabel, forgetDevice, upsertDevice, useDevices, type KnownDevice } from '../lib/devices'
import { catalogDate } from '../solo2/catalogDate'
import Dialog from './Dialog'

interface Props {
  /** Keep a downloaded session (zlib .xrz bytes) on the phone. */
  onFile: (name: string, bytes: ArrayBuffer, info: SessionInfo) => Promise<void>
  /** Names already open or stored, to mark what is on the phone already */
  known: Set<string>
}

type Phase = 'idle' | 'connecting' | 'listing' | 'ready' | 'downloading' | 'uploading'

/** Idle time after which the connection is dropped, to spare the logger's battery. */
const IDLE_MS = 120_000

/**
 * Sidebar section of the native app: connect to the Solo 2 over its Wi-Fi hotspot, list the
 * sessions on the logger and download or upload the ones the user ticks. Hidden on the
 * website, where the browser has no sockets.
 */
export default function Solo2Panel({ onFile, known }: Props) {
  const [phase, setPhase] = useState<Phase>('idle')
  const [sessions, setSessions] = useState<AimSession[]>([])
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [progress, setProgress] = useState<{ name: string; received: number; expected: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string[]>([])
  const client = useRef<AimClient | null>(null)
  const abort = useRef<AbortController | null>(null)
  // Loggers seen before, by Wi-Fi name; the one connected now; the nickname question.
  const devices = useDevices()
  const currentDevice = useRef<string | null>(null)
  const [nickPrompt, setNickPrompt] = useState<{ id: string; draft: string } | null>(null)
  const [editDevice, setEditDevice] = useState<{ device: KnownDevice; nick: string } | null>(null)
  const idleTimer = useRef<number | null>(null)
  const [autoClosed, setAutoClosed] = useState(false)

  const disconnect = useCallback(async () => {
    if (idleTimer.current) clearTimeout(idleTimer.current)
    idleTimer.current = null
    abort.current?.abort()
    abort.current = null
    const c = client.current
    client.current = null
    if (c) await c.close().catch(() => undefined)
    setPhase('idle')
    setProgress(null)
  }, [])

  useEffect(() => () => void disconnect(), [disconnect])

  /** Any action on the device's sessions restarts the idle clock. */
  // True while a download runs: the idle clock never fires then, whatever the progress reports.
  const transferring = useRef(false)
  const active = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current)
    const fire = () => {
      if (transferring.current) {
        idleTimer.current = window.setTimeout(fire, IDLE_MS)
        return
      }
      setAutoClosed(true)
      void disconnect()
    }
    idleTimer.current = window.setTimeout(fire, IDLE_MS)
  }, [disconnect])

  /** Connect to the logger; with a network name, first have iOS join that Wi-Fi. */
  async function connect(ssid?: string) {
    setError(null)
    setDone([])
    setAutoClosed(false)
    let joined = false
    if (ssid && wifiAvailable) {
      setPhase('connecting')
      try {
        await joinWifi(ssid)
        joined = true
      } catch (e) {
        setPhase('idle')
        if (!(e instanceof WifiError && e.code === 'denied')) setError(e instanceof Error ? e.message : String(e))
        return
      }
    }
    let w = await wifiStatus().catch(() => null)
    // Right after a join the address takes a moment to arrive.
    for (let i = 0; joined && i < 10 && !(w?.ip ?? '').startsWith('10.0.0.'); i++) {
      await new Promise((r) => setTimeout(r, 800))
      w = await wifiStatus().catch(() => null)
    }
    if (w && !w.wifi) {
      setError(
        `The phone is not on a Wi-Fi network (${w.status}). Open Settings > Wi-Fi and join the Solo 2 network, then come back.`,
      )
      return
    }
    if (w?.ip && !w.ip.startsWith('10.0.0.')) {
      setError(
        `The phone is on a Wi-Fi network with address ${w.ip}, not the Solo 2 hotspot (10.0.0.x). Join the Solo 2 network in Settings > Wi-Fi.`,
      )
      return
    }
    setPhase('connecting')
    const ac = new AbortController()
    abort.current = ac
    const c = new AimClient(new NativeSolo2Bridge(), ac.signal)
    client.current = c
    try {
      await c.open()
      // Remember the logger by its Wi-Fi network (when iOS tells us), so it can be rejoined later.
      const name = c.device?.name ?? 'AiM logger'
      const found = (await currentSsid()) ?? ssid ?? undefined
      const id = name
      currentDevice.current = id
      const known = upsertDevice({ id, name, ...(found ? { ssid: found } : {}) })
      if (!known.nick && !known.nickAsked) setNickPrompt({ id, draft: defaultNick(known.ssid, name) })
      setPhase('listing')
      const list = await c.sessions()
      // Newest first: by the catalog's date and time, names (sequential) breaking ties.
      list.sort((a, b) => {
        const ta = Date.parse(infoOf(a).date ?? '') || 0
        const tb = Date.parse(infoOf(b).date ?? '') || 0
        return tb - ta || b.name.localeCompare(a.name, undefined, { numeric: true, sensitivity: 'base' })
      })
      setSessions(list)
      setPicked(new Set())
      setPhase('ready')
      active()
    } catch (e) {
      // Without the Wi-Fi plugin (older builds) the phone may simply be on the wrong network.
      const hint = joined
        ? ''
        : `\nThe phone must be on the logger's Wi-Fi${ssid ? ` (${ssid})` : ''}: this version cannot switch networks by itself, so join it in Settings > Wi-Fi, then connect again.\n`
      setError(describe(e, c).replace('\n--- last protocol steps ---', `${hint}--- last protocol steps ---`))
      await disconnect()
    }
  }

  /** Download one session from the logger and keep it on the phone. Downloads run one at a time. */
  const queue = useRef<AimSession[]>([])
  async function download(s: AimSession) {
    if (queue.current.some((q) => q.name === s.name) || progress?.name === s.name) return
    active()
    queue.current.push(s)
    setPicked((p) => new Set(p).add(s.name))
    if (phase === 'downloading') return
    const c = client.current
    if (!c) return
    setError(null)
    setPhase('downloading')
    transferring.current = true
    try {
      while (queue.current.length) {
        const next = queue.current.shift()!
        setProgress({ name: next.name, received: 0, expected: next.size })
        const bytes = await c.download(next, (received, expected) => {
          active()
          setProgress({ name: next.name, received, expected })
        })
        await onFile(
          next.name,
          bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer,
          { ...infoOf(next), ...(currentDevice.current ? { device: currentDevice.current } : {}) },
        )
        setDone((d) => [...d, next.name])
        active()
        setPicked((p) => {
          const n = new Set(p)
          n.delete(next.name)
          return n
        })
      }
      setPhase('ready')
    } catch (e) {
      queue.current = []
      setPicked(new Set())
      setError(describe(e, c))
      setPhase(client.current ? 'ready' : 'idle')
    } finally {
      transferring.current = false
      setProgress(null)
      // The idle clock starts over once the last download has finished.
      active()
    }
  }

  if (!solo2Available) return null

  const busy = phase === 'downloading' || phase === 'uploading'

  const sessionFiles = sessions.filter((s) => s.supported).length
  return (
    <section className="solo2">
      {phase !== 'idle' && (
        <p className="solo2-status">
          <span className="count">{phase === 'ready' ? `Connected · ${sessionFiles} sessions` : `${phase}…`}</span>
        </p>
      )}
      {phase === 'idle' && (
        <>
          {autoClosed && (
            <p className="muted small">
              Disconnected after two minutes without activity, to save the logger's battery.
            </p>
          )}
          <button className="button" onClick={() => void connect()}>
            Connect to device on WiFi
          </button>
          {devices.length > 0 && <p className="devices-heading">Devices:</p>}
          {devices.length > 0 && (
            <ul className="cloud-list device-list">
              {devices.map((d) => (
                <li key={d.id}>
                  <div className="cloud-row">
                    <span
                      className="cloud-name"
                      title="Nickname, Wi-Fi name, remove"
                      onClick={() => setEditDevice({ device: d, nick: d.nick ?? '' })}
                    >
                      <span>
                        {deviceLabel(d)}
                        <small>{d.ssid ?? d.name}</small>
                      </span>
                    </span>
                    <button
                      className="button row-button upload-button"
                      title={
                        d.ssid && wifiAvailable
                          ? `Join ${d.ssid} and connect`
                          : 'Connect (join its Wi-Fi in Settings first)'
                      }
                      onClick={() => void connect(d.ssid)}
                    >
                      Connect
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {(phase === 'connecting' || phase === 'listing') && (
        <p className="muted small">
          {phase === 'connecting' ? 'Connecting to the logger at 10.0.0.1…' : 'Reading the session list…'}
        </p>
      )}
      {(phase === 'ready' || busy) && (
        <>
          {client.current?.device && (
            <p className="muted small">
              Connected to {client.current.device.name}. Disconnects by itself after two minutes without activity.
            </p>
          )}
          {progress && (
            <p className="muted small">
              Downloading {progress.name} · {Math.round((100 * progress.received) / Math.max(1, progress.expected))}%
            </p>
          )}
          <div className="side-row">
            <button className="button secondary" disabled={busy} onClick={() => void disconnect()}>
              Disconnect (saves battery)
            </button>
          </div>
          <p className="muted small">
            Sessions on the device ({sessionFiles}). Downloaded sessions appear under My repo.
          </p>
          {sessions.length === 0 && <p className="muted small">No sessions on the logger.</p>}
          <ul className="cloud-list">
            {sessions.map((s) => {
              const have =
                known.has(s.name) || known.has(s.name.replace(/\.(xrz|hrz)$/i, '.xrk')) || done.includes(s.name)
              const active = progress?.name === s.name
              const waiting = picked.has(s.name) && !active
              return (
                <li key={s.name}>
                  <div className="cloud-row">
                    <span className="cloud-name" title={s.name}>
                      <span>
                        {s.name}
                        {!s.supported && <small className="muted"> · not a session file</small>}
                      </span>
                    </span>
                    <small>
                      {active
                        ? `${Math.round((100 * progress!.received) / Math.max(1, progress!.expected))}%`
                        : `${(s.size / 1024).toFixed(0)} KB`}
                    </small>
                    {s.supported &&
                      (have ? (
                        <button className="button row-button upload-button secondary" disabled>
                          On phone
                        </button>
                      ) : active || waiting ? (
                        <button className="button row-button upload-button secondary" disabled>
                          {active ? 'Downloading…' : 'Queued'}
                        </button>
                      ) : (
                        <button className="button row-button upload-button" onClick={() => void download(s)}>
                          Download
                        </button>
                      ))}
                  </div>
                  <p className="muted small file-info">
                    {describeDate(infoOf(s).date) ?? s.date}
                    {s.laps != null ? ` · ${s.laps} laps` : ''}
                    {s.bestMs ? ` · best ${formatMs(s.bestMs)}` : ''}
                    {s.track ? ` · ${s.track}` : ''}
                  </p>
                </li>
              )
            })}
          </ul>
        </>
      )}
      {error && <pre className="error solo2-error">{error}</pre>}
      {editDevice && (
        <Dialog title={deviceLabel(editDevice.device)} onClose={() => setEditDevice(null)}>
          <p className="muted small">Nickname</p>
          <input
            className="nick-input"
            type="text"
            value={editDevice.nick}
            placeholder={editDevice.device.name}
            onChange={(e) => setEditDevice({ ...editDevice, nick: e.target.value })}
          />
          <p className="muted small">
            Logger: {editDevice.device.name}
            <br />
            Wi-Fi:{' '}
            <span className="device-dialog-ssid">
              {editDevice.device.ssid ?? 'not known yet (connect once from this version)'}
            </span>
          </p>
          <div className="side-row dialog-buttons">
            <button
              className="button"
              onClick={() => {
                upsertDevice({ id: editDevice.device.id, nick: editDevice.nick.trim(), nickAsked: true })
                setEditDevice(null)
              }}
            >
              Save
            </button>
            <button
              className="button secondary danger"
              onClick={() => {
                forgetDevice(editDevice.device.id)
                setEditDevice(null)
              }}
            >
              Remove this device
            </button>
          </div>
        </Dialog>
      )}
      {nickPrompt && (
        <Dialog
          title="Name this logger"
          onClose={() => {
            upsertDevice({ id: nickPrompt.id, nickAsked: true })
            setNickPrompt(null)
          }}
        >
          <p className="muted small">A short name for the logger, shown with the sessions downloaded from it.</p>
          <input
            className="nick-input"
            type="text"
            value={nickPrompt.draft}
            autoFocus
            onChange={(e) => setNickPrompt({ ...nickPrompt, draft: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
            }}
          />
          <div className="side-row dialog-buttons">
            <button
              className="button"
              onClick={() => {
                upsertDevice({ id: nickPrompt.id, nick: nickPrompt.draft.trim(), nickAsked: true })
                setNickPrompt(null)
              }}
            >
              Confirm
            </button>
            <button
              className="button secondary"
              onClick={() => {
                upsertDevice({ id: nickPrompt.id, nickAsked: true })
                setNickPrompt(null)
              }}
            >
              Skip
            </button>
          </div>
        </Dialog>
      )}
    </section>
  )
}

/** The catalog's dd/mm/yyyy + hh:mm:ss as the repo's per-file info. */
function infoOf(s: AimSession): SessionInfo {
  const date = catalogDate(s.date, s.time)
  return {
    date: date ? date.toISOString() : undefined,
    laps: s.laps,
    bestMs: s.bestMs,
    track: s.track,
  }
}

function describeDate(iso: string | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return formatDateTime(d)
}

function formatMs(ms: number): string {
  const s = ms / 1000
  const m = Math.floor(s / 60)
  return `${m}:${(s - m * 60).toFixed(3).padStart(6, '0')}`
}

function describe(e: unknown, c: AimClient): string {
  const base = e instanceof AimError ? `${e.message} [${e.code}]` : e instanceof Error ? e.message : String(e)
  const tail = c.log.length ? `\n--- last protocol steps ---\n${c.log.slice(-24).join('\n')}` : ''
  return base + tail
}
